import { useCallback } from 'react';

import { Box, IconButton, Tooltip } from '@mui/material';

import MapPinIcon from '@/components/Icons/MapPinIcon';
import MapPinnedIcon from '@/components/Icons/MapPinnedIcon';
import { MAX_PINNED_IDS } from '@/constants';
import useUser from '@/hooks/useUser';
import { WorkspaceSections } from '@/sections/Sidebar/types';
import { useSnackbar } from 'notistack';
import { useRecoilState } from 'recoil';

import { pinnedIdsState } from '../atoms';
import { sortPinnedIds } from '../utils';
import { useWorkspaceOrgSharedEndpointsQuery } from './__generated__/queries.generated';

const POLL_INTERVAL = 15000;

function useOrgSharedEndpoints(poll: boolean = false) {
  const { data, loading } = useWorkspaceOrgSharedEndpointsQuery({
    fetchPolicy: 'cache-first',
    pollInterval: poll ? POLL_INTERVAL : null,
  });

  return {
    orgSharedEndpoints: data?.organizationSharedEndpoints || [],
    loading,
  };
}

function usePinnedAPI() {
  const user = useUser();
  const { enqueueSnackbar } = useSnackbar();
  const [pinnedIds, setPinnedIds] = useRecoilState(
    pinnedIdsState(`${WorkspaceSections.ModelAPIs}-${user.id}`),
  );
  const { orgSharedEndpoints, loading } = useOrgSharedEndpoints();

  const pinnedEndpoints = orgSharedEndpoints
    .filter((endpoint) => pinnedIds.includes(endpoint.id))
    .sort((a, b) => sortPinnedIds(pinnedIds, a.id, b.id));

  const togglePinEntity = useCallback(
    (entityId: string, endpointName: string) => {
      setPinnedIds((prev) => {
        if (prev.includes(entityId)) {
          return prev.filter((id) => id !== entityId);
        }
        enqueueSnackbar(`Pinned ${endpointName} to the sidebar`, { variant: 'success' });
        return [entityId, ...prev];
      });
    },
    [setPinnedIds, enqueueSnackbar],
  );

  const canPinMoreEndpoints = pinnedEndpoints.length < MAX_PINNED_IDS;

  function stopPropagation(ev: React.MouseEvent) {
    ev.preventDefault();
    ev.stopPropagation();
  }

  function getPinActionItem(entityId: string, endpointName: string) {
    const isPinned = pinnedIds.includes(entityId);
    const isPinDisabled = !canPinMoreEndpoints && !isPinned;

    return (
      <Tooltip
        title={
          isPinDisabled
            ? `Limit ${MAX_PINNED_IDS} pins`
            : isPinned
            ? 'Unpin from sidebar'
            : 'Pin to sidebar'
        }
      >
        <Box>
          <IconButton
            onClick={(e) => {
              stopPropagation(e);
              togglePinEntity(entityId, endpointName);
            }}
            data-cy="pin-entity-btn"
            disabled={isPinDisabled}
          >
            {isPinned ? <MapPinnedIcon color="inherit" /> : <MapPinIcon color="secondary" />}
          </IconButton>
        </Box>
      </Tooltip>
    );
  }

  return {
    pinnedIds,
    togglePinEntity,
    canPinMoreEndpoints,
    getPinActionItem,
    loading,
    pinnedEndpoints,
  };
}

export { useOrgSharedEndpoints, usePinnedAPI };
